import React from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Money as Money,
  core_apimessages_Error as Error,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '../../src/schemas/rql';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { inMobileBrowser } from '@reverbdotcom/commons/src/user_context_helpers';
import CondensedOrderSummary from './condensed_order_summary';
import { useApplePayAvailabilityByConfig } from './useApplePayAvailability';
import experiments from '@reverbdotcom/commons/src/experiments';
import { CheckoutErrorAlertBox } from './CheckoutErrorAlertBox';
import CartApplePayButton from './CartApplePayButton';
import CheckoutPayPalButton, { isCartEligible } from './CheckoutPayPalButton';
import classNames from 'classnames';

interface Props {
  paymentMethodType: string;
  checkoutBundlingId: string;
  applePayCheckoutBundlingId: string;
  amountTotal: Money
  adyenCheckoutPaymentMethodsConfig: string;
  currency: string;
  paypalCheckoutExpAttributes: {
    currencyCount: number,
    bundleCount: number,
    isLocalPickupOnly: boolean,
    includesStandalonePaypal: boolean,
  }
}

export default function CartHoistedExpressPayWrapper(props: Props) {
  const user = useUser();
  const [errors, setErrors] = React.useState([]);

  const [isApplePayAvailable] = useApplePayAvailabilityByConfig(props.adyenCheckoutPaymentMethodsConfig);
  const showApplePayButton = showHoistedButton(user) && isApplePayAvailable && props.paymentMethodType == CheckoutPaymentMethodType.DIRECT_CHECKOUT;

  const isCartEligibleForPaypalExp = isCartEligible(props.paypalCheckoutExpAttributes);
  const inPaypalButtonRollout = useExpEnabled(experiments.PAYPAL_CHECKOUT_BUTTON);
  const showPaypalButton = inPaypalButtonRollout && isCartEligibleForPaypalExp;

  const showExpressPay = inPaypalButtonRollout && inMobileBrowser(user)
    && (showApplePayButton || showPaypalButton);

  const classes = classNames(
    'checkout-cart-header',
    { 'checkout-cart-header--express': inPaypalButtonRollout },
  );

  if (!showExpressPay && !showApplePayButton) {
    return null;
  }

  return (
    <div className={classes}>
      <CondensedOrderSummary
        itemPlusShippingSubtotal={props.amountTotal.display}
      />
      {showExpressPay &&
        <>
          {errors.length > 0 &&
            <div className="mb-2">
              <CheckoutErrorAlertBox errors={errors} />
            </div>
          }
          <CartExpressPayWrapper
            amountTotal={props.amountTotal}
            adyenCheckoutPaymentMethodsConfig={props.adyenCheckoutPaymentMethodsConfig}
            showPaypalCheckoutButton={showPaypalButton}
            applePayCheckoutBundlingId={props.applePayCheckoutBundlingId}
            checkoutBundlingId={props.checkoutBundlingId}
            onError={setErrors}
            currency={props.currency}
          />
        </>
      }
      {!showExpressPay && showApplePayButton &&
        <div className="checkout-cart-header__button">
          <div className="checkout-cart-header__button--apple-pay">
            <CartApplePayButton
              variant="black"
              checkoutBundlingId={props.checkoutBundlingId}
              amountTotal={props.amountTotal}
              adyenCheckoutPaymentMethodsConfig={props.adyenCheckoutPaymentMethodsConfig}
              tracking={{
                displayStyle: 'hoisted',
              }}
            />
          </div>
        </div>
      }
    </div>
  );
}

function showHoistedButton(user: Partial<IUser>) {
  return user.countryCode === 'US'
    && inMobileBrowser(user);
}

interface ExpressPayWrapperProps {
  applePayCheckoutBundlingId: string;
  adyenCheckoutPaymentMethodsConfig: string;
  showPaypalCheckoutButton: boolean;
  onError: (errors: Error[]) => void;
  checkoutBundlingId: string;
  amountTotal: Money;
  currency: string;
}

export function CartExpressPayWrapper(props: ExpressPayWrapperProps) {
  return (
    <fieldset className="bd-1 bd-color-primary mb-2 pb-2 pt-2 plr-2">
      <legend className="weight-bold mlr-auto">{I18n.t('discovery.checkout.expressPay.title')}</legend>
      <div className="mb-1">
        {props.showPaypalCheckoutButton &&
          <CheckoutPayPalButton
            currency={props.currency}
            checkoutBundlingId={props.checkoutBundlingId}
            onError={props.onError}
            variant="cart"
            parentComponentName="CartHoistedExpressPayWrapper"
          />
        }
      </div>
      <CartApplePayButton
        variant="black"
        checkoutBundlingId={props.applePayCheckoutBundlingId}
        amountTotal={props.amountTotal}
        adyenCheckoutPaymentMethodsConfig={props.adyenCheckoutPaymentMethodsConfig}
        tracking={{
          displayStyle: 'hoisted',
        }}
      />
    </fieldset>
  );
}
